import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Button,
  Collapse,
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// ⚡ Path-based imports (6× faster than barrel imports)
import MusicIcon from '@mui/icons-material/MusicNote';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AudioMetadata, isUsefulSpotifyQuery } from '@/lib/audioMetadata';
import { analytics } from '@/lib/analytics';

interface SpotifyTrack {
  id: string;
  uri: string;
  name: string;
  artists: Array<{ name: string }>;
  album: {
    name: string;
    images: Array<{ url: string; height: number; width: number }>;
  };
  duration_ms: number;
}

interface SpotifyMatchSuggestionsProps {
  metadata: AudioMetadata;
  filename: string;
  searchQuery: string;
  accessToken?: string | null;
  onTrackSelect: (track: SpotifyTrack) => void;
  onDismiss?: () => void;
}

export function SpotifyMatchSuggestions({
  metadata,
  filename,
  searchQuery,
  accessToken,
  onTrackSelect,
  onDismiss,
}: SpotifyMatchSuggestionsProps) {
  const queryIsUseful = isUsefulSpotifyQuery(searchQuery);
  const [inputValue, setInputValue] = useState(queryIsUseful ? searchQuery : '');
  const [debouncedQuery, setDebouncedQuery] = useState(
    queryIsUseful ? searchQuery.trim() : '',
  );
  const [results, setResults] = useState<SpotifyTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const useful = isUsefulSpotifyQuery(searchQuery);
    setInputValue(useful ? searchQuery : '');
    setDebouncedQuery(useful ? searchQuery.trim() : '');
  }, [searchQuery]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = inputValue.trim();
      setDebouncedQuery(trimmed.length >= 2 ? trimmed : '');
    }, 350);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (dismissed) return;

    if (!debouncedQuery) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();

    const searchSpotify = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({ q: debouncedQuery });
        if (accessToken) {
          params.append('token', accessToken);
        }

        const response = await fetch(`/api/spotify/search?${params}`, {
          signal: controller.signal,
        });
        const data = await response.json();

        if (controller.signal.aborted) return;

        if (data.error) {
          setError(data.error);
          setResults([]);
          analytics.searchExecuted(debouncedQuery, 'spotify', 0);
        } else {
          const items = data.items?.slice(0, 5) || [];
          setResults(items);
          analytics.searchExecuted(debouncedQuery, 'spotify', items.length);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error('Failed to search Spotify:', err);
        setError('Failed to search Spotify');
        setResults([]);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    searchSpotify();
    return () => controller.abort();
  }, [debouncedQuery, accessToken, dismissed]);

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  if (dismissed) return null;

  const formatDuration = (ms: number) => {
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const hasMetadata = Boolean(metadata.title || metadata.artist);
  const showTypeaheadHint = !hasMetadata && !queryIsUseful;

  return (
    <Collapse in={!dismissed}>
      <Paper
        sx={{
          p: 3,
          mb: 3,
          bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.success.main, 0.3),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SearchIcon color="success" />
            <Typography variant="h6" fontWeight="bold">
              Find on Spotify
            </Typography>
          </Box>
          <Button
            size="small"
            onClick={handleDismiss}
            startIcon={<CloseIcon />}
            sx={{ minWidth: 'auto' }}
          >
            Dismiss
          </Button>
        </Box>

        {hasMetadata ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Found metadata in file:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {metadata.title && (
                <Chip label={`Title: ${metadata.title}`} size="small" color="success" variant="outlined" />
              )}
              {metadata.artist && (
                <Chip label={`Artist: ${metadata.artist}`} size="small" color="success" variant="outlined" />
              )}
              {metadata.album && (
                <Chip label={`Album: ${metadata.album}`} size="small" color="default" variant="outlined" />
              )}
            </Box>
          </Box>
        ) : showTypeaheadHint ? (
          <Typography variant="body2" color="text.secondary" mb={2}>
            No metadata or a useful filename — type a song, artist, or album to search Spotify.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" mb={2}>
            No metadata found, searching by filename: "{filename}"
          </Typography>
        )}

        <TextField
          fullWidth
          size="small"
          autoFocus={showTypeaheadHint}
          placeholder="Search Spotify for a song, artist, or album"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {loading ? <CircularProgress size={18} /> : <SearchIcon fontSize="small" />}
              </InputAdornment>
            ),
            endAdornment: inputValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    setInputValue('');
                    setResults([]);
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!debouncedQuery && !error && (
          <Alert severity="info">
            Start typing to look up a track on Spotify.
          </Alert>
        )}

        {!loading && debouncedQuery && results.length === 0 && !error && (
          <Alert severity="info">
            No Spotify matches for "{debouncedQuery}". Try a different song or artist name.
          </Alert>
        )}

        {results.length > 0 && (
          <>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Select a match to play on Spotify and compare analysis:
            </Typography>
            <List sx={{ bgcolor: 'background.paper', borderRadius: 1 }}>
              {results.map((track, index) => (
                <ListItem key={track.id} disablePadding>
                  <ListItemButton onClick={() => {
                    analytics.spotifyTrackSelected(track.name, 'match');
                    onTrackSelect(track);
                  }}>
                    <ListItemAvatar>
                      <Avatar
                        src={track.album.images[2]?.url || track.album.images[0]?.url}
                        alt={track.album.name}
                        variant="rounded"
                      >
                        <MusicIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {track.name}
                          {index === 0 && (
                            <Chip label="Best match" size="small" color="success" />
                          )}
                        </Box>
                      }
                      secondary={`${track.artists.map((a) => a.name).join(', ')} • ${track.album.name} • ${formatDuration(track.duration_ms)}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Paper>
    </Collapse>
  );
}
